import React from 'react';

import { cn } from '../../../design-system/lib/cn';
import { useViewport } from '../../../design-system/hooks/useViewport';
import { Segmented } from '../../../design-system/components/desktop';
import { SegmentedControl, SegmentedControlItem } from '../../../design-system/components/mobile';
import { AuthInput } from '../../../design-system/patterns/auth';

import './create-listing.css';

/**
 * The shapes every create-listing step shares.
 *
 * They live here rather than in `create-listing.view.jsx` so the step views can
 * import them without importing the page that renders them, which would be a
 * cycle. Nothing here fetches, navigates or toasts.
 */

/**
 * A step panel: kicker, title, sub, body.
 *
 * The title carries `tabIndex={-1}` and is focused by the page after a step
 * change, so a screen reader lands on the new panel instead of staying on the
 * Next button that no longer exists. `.dsa-cl-panel__title:focus` paints no ring
 * because that is programmatic focus, not keyboard focus.
 */
export const WizardPanel = React.forwardRef(function WizardPanel(
  { step, total = 4, title, sub, children, className, ...rest },
  ref,
) {
  return (
    <section className={cn('dsa-cl-panel', className)} {...rest}>
      {step ? (
        <div className="dsa-cl-panel__kicker">
          Step {step} of {total}
        </div>
      ) : null}
      <h2 ref={ref} className="dsa-cl-panel__title" tabIndex={-1}>
        {title}
      </h2>
      {sub ? <p className="dsa-cl-panel__sub">{sub}</p> : null}
      <div className="dsa-cl-panel__body">{children}</div>
    </section>
  );
});

/**
 * A Rand input.
 *
 * The `R` is an affix on the field, not a character inside the value, so the
 * value that reaches `formData` stays a plain number string and the submit
 * payload is unchanged. Prices are Rands everywhere in this product; there is
 * no currency prop and no dollar sign anywhere in this directory.
 */
export const MoneyInput = React.forwardRef(function MoneyInput({ className, ...rest }, ref) {
  return (
    <span className="dsa-cl-money">
      <span className="dsa-cl-money__cur" aria-hidden="true">
        R
      </span>
      <AuthInput ref={ref} inputMode="decimal" className={className} {...rest} />
    </span>
  );
});

/**
 * The wizard's segmented control, picking its track.
 *
 * MOBILE CHECK, STEP 3. The step used the desktop `Segmented` on every
 * viewport, and on a phone that is the wrong control twice over.
 *
 * `.dsk-segmented` is `inline-flex` with `white-space: nowrap` buttons, so it
 * is shrink-to-fit and cannot wrap. Measured at 320px, the narrowest viewport
 * the app supports: the shell takes 16px of gutter each side and
 * `.dsa-cl-panel` another 24px, leaving 240px of column. "Fixed price" and
 * "Market-tracked" at 14px semibold with `0 16px` of padding each measure
 * about 241px together, so the pricing-mode control overflows its own panel
 * by a hair and any font fallback makes that worse. Every other control in
 * the step is full width, so it also reads as a stray box parked on the left.
 *
 * Its buttons are 36px tall, under the 44px the mobile track holds elsewhere
 * (`.ds-qty-stepper` is the reference at 44px).
 *
 * `.ds-segmented` answers both: `display: flex` with `flex: 1` segments, so it
 * fills the column and the labels get the whole width to divide between them,
 * at 38px min-height — the mobile track's own value, set in the vendored
 * `mobile/molecules.css` and not ours to raise.
 *
 * This is a real DOM difference (two class trees, two components), which is
 * the only thing `useViewport()` is for; a media query cannot swap components.
 * Both tracks emit real `<button>`s carrying `aria-pressed`, so the accessible
 * name and the pressed state are identical either side of the handoff.
 *
 * `id` and `error` are swallowed, for the reason `FieldSlot` below swallows
 * them: `ModalField` clones both onto its single child expecting an `<input>`,
 * and neither means anything on a `role="group"` div — `htmlFor` cannot
 * associate a label with one, and `error={false}` on a div is a React
 * non-boolean-attribute warning. The group is named by `aria-label` instead,
 * which is how a set of buttons gets a name. The aria state is kept.
 */
export function WizardSegmented({ id, error, label, value, onChange, items = [], ...rest }) {
  const { isDesktop } = useViewport();

  const aria = {
    'aria-label': label,
    'aria-describedby': rest['aria-describedby'],
    'aria-invalid': rest['aria-invalid'],
  };

  if (isDesktop) {
    return <Segmented value={value} onChange={onChange} items={items} {...aria} />;
  }

  return (
    <SegmentedControl role="group" {...aria}>
      {items.map((item) => (
        <SegmentedControlItem
          key={item.value}
          active={item.value === value}
          onClick={() => onChange && onChange(item.value, item)}
        >
          {item.label}
        </SegmentedControlItem>
      ))}
    </SegmentedControl>
  );
}

/**
 * A slot for a COMPOSITE control inside a labelled field.
 *
 * `ModalField` clones `id`, `error` and the aria wiring onto its single child,
 * which is exactly right for one input and exactly wrong for two controls that
 * together make one answer. This swallows those props and keeps the aria state
 * on a wrapper, so nothing lands on a DOM node that would not understand it.
 */
export const FieldSlot = React.forwardRef(function FieldSlot(
  { id, error, children, className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn('dsa-cl-slot', className)}
      aria-describedby={rest['aria-describedby']}
      aria-invalid={rest['aria-invalid']}
    >
      {children}
    </div>
  );
});

/**
 * A figure the form is reasoning about rather than one it collects: the
 * platform fee, the market price the seller is pricing against.
 */
export function Readout({ label, value }) {
  return (
    <div className="dsa-cl-readout">
      <span className="dsa-cl-readout__label">{label}</span>
      <span className="dsa-cl-readout__value">{value}</span>
    </div>
  );
}
